<script lang="ts">
    import { derived as sderived, writable, type Writable } from 'svelte/store'

    import { cloneDeep } from 'lodash-es'
    import { toASCII } from 'punycode-esm'
    import * as yup from 'yup'

    import type { CrumbArray } from 'hydria/components/breadcrumb'
    import { HydriaDocument } from 'hydria/HydriaDocument'
    import type { IDocument, IRevision } from 'hydria/HydriaDocument'
    import { gettext as _, resolve, tgettext } from 'hydria/services/gettext'

    import { Breadcrumb } from 'breadcrumb'

    import 'codemirror/mode/jinja2/jinja2'

    import { onDestroy, setContext, tick } from 'svelte'

    import Icon from '@iconify/svelte'

    import { iconExclamationTriangle } from 'hydria/icons'
    import { simpleFetch } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'
    import { spath } from 'hydria/svelte_helpers'

    import CodeMirror from 'admin/components/CodeMirror.svelte'
    import DocumentEdit from 'hydria/components/DocumentEdit.svelte'
    import LanguageSelector from 'hydria/components/Generics/LanguageSelector.svelte'
    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    interface Props {
        route: any
    }

    let { route }: Props = $props()
    const revFields = ['title', 'content', 'lg', 'filename']

    let testVars = $state({
        zones: [] as any[],
        domains: [] as any[],
        site_name: 'WebDomaine.ca',
        lg: 'fr',
        test_mode: '',
        site_url: 'https://www.wdom-dev-02.l.cogescient.ca/',
        // site_url: 'https://www.webdomaine.ca/',
        notification: {
            days_delta: 90,
        },
        epp_auth_password: 'EPPCODE',
        domain_unicode: 'my-cute-domain-name.ca',
        valid_until_datetime: 'date:future:days:7',
        token_url: 'https://www.webdomaine.ca/token-url/abab7878',
        new_email: 'new@arcinfo.qc.ca',
        old_email: 'old@arcinfo.qc.ca',
        order_url: 'https://www.webdomaine.ca/order-url/abab7878',
    })
    let testMode = $state('html')
    let testTmpl = null
    let testError: string | null = $state(null)
    let testResult: string | undefined = $state('')

    let domPool = ['arcinformatique.ca', 'sirops-des-forêts.com', 'dorémi.music', 'this-is-a-ridiculous-domain.photography', 'la-tourte-enflammée.ca', 'arcinformatique.ca', 'how-to-do-stuff.net', 'another-realm.com', 'potato.com']

    interface ILocalDocument extends IDocument {}
    interface ILocalRevision extends IRevision {
        title: string
        content: string
        filename: string
    }

    class LocalDocument extends HydriaDocument {
        docType = 'jinja_template'
        docTypePlural = 'jinja_templates'

        docTypeName = tgettext('Jinja template')
        docTypeNamePlural = tgettext('Jinja templates')
    }

    const Document = new LocalDocument()
    const status = sderived(Document.status, ($status) => $status)

    const revSaved = sderived(Document.revision, ($rev) => {
        const data = (!$rev ? $rev : Object.fromEntries(Object.entries($rev).filter(([k, v]) => revFields.includes(k)))) as ILocalRevision
        if (data != null && data.content == null) {
            data.content = ''
        }
        return data
    })
    const docSaved = sderived(Document.document, ($rev) => ({ ...$rev }) as ILocalDocument)

    // revEdit is actually handled in DocumentEdit.
    let revEdit: Writable<ILocalRevision | undefined> = writable(undefined)
    setContext('revEdit', revEdit)

    // Helper function to simplify that try/catch bullshit when we only want a boolean
    function validateSyncAt(schema: any, key: string, form: any) {
        try {
            schema.validateSyncAt(key, form)
            return true
        } catch {
            return false
        }
    }

    const revSchema = yup.object().shape({
        title: yup.string().required(),
        content: yup.string().required(),
        lg: yup
            .string()
            .required()
            .test('lgIsValid', '${path} is not valid', (value) => value === 'fr' || value === 'en'),
        filename: yup.string().required(),
    })

    function compGroupClasses(orig: any, edit: any, field: string, schema: any) {
        if (!validateSyncAt(schema, field, edit)) return 'is-invalid'
        if (orig[field] !== edit[field]) return 'is-warning'
        return ''
    }

    const crumbs = sderived(status, ($status) => {
        const c: CrumbArray = [{ txt: Document.docTypeNamePlural, url: spath(`/jinja-templates`) }]
        switch ($status) {
            case 'loading':
                c.push({
                    txt: '…',
                })
                break
            case 'error':
            case 'not-found':
                c.push({
                    html: true,
                    txt: iconExclamationTriangle,
                })
                break
            case 'ready':
                c.push({
                    txt: tgettext('Edit'),
                })
                c.push({
                    txt: `<span class="text-muted">${Document.docId}</span>`,
                    html: true,
                })
        }
        return c
    })

    function testClearVar(name: string) {
        delete testVars[name]
        testTemplatePreview()
    }
    function testSetVar(name: string, value: any) {
        testVars[name] = value
        testTemplatePreview()
    }
    function testTemplateMode(mode: string) {
        testMode = mode
        testTemplatePreview()
    }
    function testTemplateDelta(n: number) {
        testVars.notification.days_delta = n
        testTemplatePreview()
    }

    async function testTemplateZones(n: number) {
        if (n > domPool.length) n = domPool.length
        else if (n < 0) n = 0

        testVars.zones = []
        if (n) {
            for (const uni of domPool) {
                if (testVars.zones.length >= n) break
                testVars.zones.push({
                    idna: toASCII(uni),
                    unicode: uni,
                    id: 'b15b4297-d73b-4521-b2af-717b98d0692b',
                    expiration: `date:future:days:${1 + Math.floor(Math.random() * 89)}`,
                    notification: {},
                })
            }
        }
        testTemplatePreview()
    }
    async function testTemplateDomains(n: number) {
        if (n > domPool.length) n = domPool.length
        else if (n < 0) n = 0

        testVars.domains = []
        if (n) {
            for (const uni of domPool) {
                if (testVars.domains.length >= n) break
                testVars.domains.push({
                    idna: toASCII(uni),
                    unicode: uni,
                    id: 'b15b4297-d73b-4521-b2af-717b98d0692b',
                    expiration: `date:future:days:${1 + Math.floor(Math.random() * 89)}`,
                    notification: {},
                })
            }
        }
        testTemplatePreview()
    }

    let testTimer: ReturnType<typeof setTimeout> | undefined = undefined
    let testStatus: 'ready' | 'loading' = $state('ready')
    let iFrameElement: HTMLIFrameElement | undefined = $state()

    function clearTestTimer() {
        if (testTimer) {
            clearTimeout(testTimer)
        }
        testTimer = undefined
    }
    function setTestTimer() {
        clearTestTimer()
        testTimer = setTimeout(() => {
            testTimer = undefined
            testTemplatePreview()
        }, 1250)
    }

    async function testTemplatePreview() {
        clearTestTimer()
        if (!$revEdit) return

        testStatus = 'loading'
        const params = cloneDeep(testVars)
        params.lg = $revEdit.lg
        params.test_mode = testMode

        let r: SimpleFetchResult
        try {
            r = await simpleFetch('POST', '/json/general/1/jinja2-preview', { params: params, jinja2: $revEdit.content })
        } catch (e) {
            console.error('Failed to load test result:', e)
            testStatus = 'ready'
            return
        }

        if (r.data.ecode != 'success') {
            testError = r.data.error
            testResult = undefined
        } else {
            testError = null
            testResult = r.data.output
            if (testMode == 'html') {
                tick().then(() => {
                    let doc = iFrameElement?.contentWindow?.document
                    if (doc) {
                        doc.open()
                        doc.write(`<!DOCTYPE html>\n${r.data.output}`)
                        doc.close()
                    }
                })
            }
        }
        testStatus = 'ready'
    }
    let inDocId = $derived(route.result.path.params?.docId)
    let inRevId = $derived(route.result.path.params?.revId)
    let formIsValid = $derived(revSchema.isValidSync($revEdit))

    unsubs.push(
        revEdit.subscribe(($revEdit) => {
            if ($status == 'ready' && $revEdit && $revEdit.content) setTestTimer()
        }),
    )
</script>

<Breadcrumb {crumbs} />

<DocumentEdit {Document} docId={inDocId} revId={inRevId} {formIsValid} {revFields} listUrl="/jinja-templates">
    {#if $revEdit}
        <div class="card card-content">
            <div class="card-header text-bg-primary">{$_('Document')}</div>
            <div class="card-body d-grid gap-3">
                <div class="row">
                    <label for="f-title" class="col-form-label text-end col-sm-2">{$_('Title')}</label>
                    <div class="col-sm-10">
                        <input type="text" class="form-control {compGroupClasses($revSaved, $revEdit, 'title', revSchema)}" id="f-title" bind:value={$revEdit.title} />
                    </div>
                </div>
                <LanguageSelector bind:value={$revEdit.lg} />
                <div class="row">
                    <label for="f-filename" class="col-form-label text-end col-sm-2">{$_('Template file name')}</label>
                    <div class="col-sm-10">
                        <input type="text" class="form-control {compGroupClasses($revSaved, $revEdit, 'filename', revSchema)}" id="f-filename" bind:value={$revEdit.filename} />
                    </div>
                </div>
            </div>
        </div>
        <div class="card card-primary card-content">
            <div class="card-header text-bg-primary">
                {$_('Contenu')}
            </div>
            <div class="card-body card-tinymce">
                <CodeMirror class="md-editor-big" options={{ theme: 'ttcn', lineWrapping: true, lineNumbers: true, readOnly: false, mode: { name: 'jinja2' } }} bind:value={$revEdit.content} />
            </div>
        </div>

        <div class="card card-default">
            <div class="card-header text-bg-primary">Testing</div>
            <div class="card-body d-grid gap-3">
                <div class="row">
                    <div class="col-sm-3 col-form-label text-end">Global</div>
                    <div class="col-sm-9">
                        <div class="d-flex gap-2 btn-sm">
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" type="button" disabled>Mode :</button>
                                <button class="btn btn-outline-secondary" type="button" onclick={() => testTemplateMode('html')} class:active={testMode == 'html'}>HTML</button>
                                <button class="btn btn-outline-secondary" type="button" onclick={() => testTemplateMode('text')} class:active={testMode == 'text'}>Texte</button>
                                <button class="btn btn-outline-secondary" type="button" onclick={() => testTemplateMode('sms')} class:active={testMode == 'sms'}>SMS</button>
                                <button class="btn btn-outline-secondary" type="button" onclick={() => testTemplateMode('subject')} class:active={testMode == 'subject'}>Subject</button>
                            </div>
                            <button type="button" class="btn btn-outline-secondary btn-sm" onclick={() => testTemplatePreview()}><Icon inline icon="fa:refresh" /></button>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-3 col-form-label text-end">Expiration notifs</div>
                    <div class="col-sm-9">
                        <div class="d-flex gap-2 btn-sm">
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" type="button" disabled>Zones :</button>
                                {#each domPool as d, i}
                                    <button class="btn btn-outline-secondary" type="button" onclick={() => testTemplateZones(i)} class:active={i == testVars.zones.length}>{i}</button>
                                {/each}
                            </div>
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" type="button" disabled>Doms :</button>
                                {#each domPool as d, i}
                                    <button class="btn btn-outline-secondary" type="button" onclick={() => testTemplateDomains(i)} class:active={i == testVars.domains.length}>{i}</button>
                                {/each}
                            </div>
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" type="button" disabled>Jours :</button>
                                {#each [1, 15, 90] as d}
                                    <button class="btn btn-outline-secondary" type="button" onclick={() => testTemplateDelta(d)} class:active={d == testVars.notification.days_delta}>{d}</button>
                                {/each}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-sm-3 col-form-label text-end">Général</div>
                    <div class="col-sm-9">
                        <div class="d-flex gap-2 btn-sm">
                            <div class="btn-group btn-group-sm">
                                <button class="btn btn-outline-secondary" type="button" disabled>user_name</button>
                                <button class="btn btn-outline-secondary" type="button" onclick={() => testClearVar('user_name')}><Icon inline icon="fa:ban" /></button>
                                <button class="btn btn-outline-secondary" type="button" onclick={() => testSetVar('user_name', 'John Smith')}>John Smith</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="card-body" style="height: 500px;">
                {#if testStatus == 'loading'}
                    <LoadingPattern pattern={['x', 'x', 'x']} />
                {:else if testError}
                    <pre class="text-danger" style="width: 100%; height: 100%; white-space: pre-line;">{testError}</pre>
                {:else if testMode == 'html'}
                    <iframe bind:this={iFrameElement} frameborder="0" style="width: 100%; height: 100%; border: 1px solid #aaa; border-radius: 3px;" title="Preview"></iframe>
                {:else if ['text', 'sms', 'subject'].includes(testMode) && testResult}
                    <CodeMirror style="width: 100%; height: 100%;" value={testResult} />
                {/if}
            </div>
        </div>
    {/if}
</DocumentEdit>
